import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { authFetch } from '../lib/api'

const STORAGE_KEY = 'cdk-admin-auth'

export interface NavMenuItem {
  code: string
  name: string
  path: string
  icon?: string
  sort: number
}

interface StoredAuth {
  token?: string
  username: string
  name: string
  expiresAt: string
  role?: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref('')
  const username = ref('')
  const name = ref('')
  const expiresAt = ref('')
  const role = ref('')
  const isSuper = ref(false)
  const perms = ref<string[]>([])
  const menus = ref<NavMenuItem[]>([])
  const aclLoaded = ref(false)

  const isLoggedIn = computed(() => {
    if (!username.value || !expiresAt.value) return false
    return new Date(expiresAt.value).getTime() > Date.now()
  })

  const can = (code: string) => {
    if (isSuper.value) return true
    return perms.value.includes(code)
  }

  const canPage = (pageCode: string) => {
    if (isSuper.value) return true
    if (can(pageCode) || can(`${pageCode}.view`)) return true
    return perms.value.some((p) => p.startsWith(`${pageCode}.`))
  }

  const restore = () => {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    try {
      const saved = JSON.parse(raw) as StoredAuth
      token.value = saved.token || ''
      username.value = saved.username
      name.value = saved.name
      expiresAt.value = saved.expiresAt
      role.value = saved.role || ''
      if (!isLoggedIn.value) logout()
    } catch {
      logout()
    }
  }

  const save = (payload: StoredAuth) => {
    token.value = payload.token || ''
    username.value = payload.username
    name.value = payload.name
    expiresAt.value = payload.expiresAt
    role.value = payload.role || ''
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }

  const applyACL = (data: {
    role?: string
    is_super?: boolean
    menus?: NavMenuItem[]
    perms?: string[]
  }) => {
    if (data.role) role.value = data.role
    isSuper.value = !!data.is_super
    menus.value = Array.isArray(data.menus) ? data.menus : []
    perms.value = Array.isArray(data.perms) ? data.perms : []
    aclLoaded.value = true
  }

  const loadMe = async () => {
    if (!isLoggedIn.value) return false
    try {
      const r = await authFetch('/api/v1/auth/admin/me')
      if (!r.ok) return false
      const data = await r.json()
      applyACL(data)
      if (data.display_name) name.value = data.display_name
      if (data.username) username.value = data.username
      if (data.role) {
        role.value = data.role
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
          try {
            const saved = JSON.parse(raw) as StoredAuth
            saved.role = data.role
            localStorage.setItem(STORAGE_KEY, JSON.stringify(saved))
          } catch { /* ignore */ }
        }
      }
      return true
    } catch {
      return false
    }
  }

  const logout = () => {
    token.value = ''
    username.value = ''
    name.value = ''
    expiresAt.value = ''
    role.value = ''
    isSuper.value = false
    perms.value = []
    menus.value = []
    aclLoaded.value = false
    localStorage.removeItem(STORAGE_KEY)
  }

  return {
    token,
    username,
    name,
    expiresAt,
    role,
    isSuper,
    perms,
    menus,
    aclLoaded,
    isLoggedIn,
    can,
    canPage,
    restore,
    save,
    applyACL,
    loadMe,
    logout,
  }
})
